import emailjs from "@emailjs/browser";
import React from "react";
import { X, MessageCircle, Send } from "lucide-react";

function ApplyModal({ show, onClose, lang, accentColor, courseTitle }) {
  const [form, setForm] = React.useState({ name: "", phone: "", email: "", message: "" });
  const [sending, setSending] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [error, setError] = React.useState("");

  const L = {
    heading:     lang === "az" ? "Müraciət göndərin"                          : lang === "ru" ? "Отправить заявку"                         : "Send an Application",
    sub:         lang === "az" ? "Formu doldurun, komanda qısa zamanda sizinlə əlaqə saxlayacaq." : lang === "ru" ? "Заполните форму, команда свяжется с вами в ближайшее время." : "Fill the form and our team will contact you shortly.",
    name:        lang === "az" ? "Ad soyad"        : lang === "ru" ? "Имя и фамилия"   : "Full Name",
    phone:       lang === "az" ? "Telefon nömrəsi" : lang === "ru" ? "Номер телефона"  : "Phone Number",
    email:       lang === "az" ? "Elektron poçt"   : lang === "ru" ? "E-mail"          : "Email",
    message:     lang === "az" ? "Müraciət Mətni"  : lang === "ru" ? "Сообщение"       : "Message",
    submit:      lang === "az" ? "Müraciət et"      : lang === "ru" ? "Отправить"       : "Apply Now",
    sending:     lang === "az" ? "Göndərilir..."   : lang === "ru" ? "Отправка..."     : "Sending...",
    success:     lang === "az" ? "Müraciətiniz göndərildi! Tezliklə əlaqə saxlayacağıq." : lang === "ru" ? "Заявка отправлена! Мы свяжемся с вами." : "Application sent! We will contact you shortly.",
    waBtn:       lang === "az" ? "WhatsApp-a keç"  : lang === "ru" ? "Написать в WhatsApp" : "Continue on WhatsApp",
    nameErr:     lang === "az" ? "Ad soyad daxil edin" : lang === "ru" ? "Введите имя" : "Enter your name",
    phoneErr:    lang === "az" ? "Telefon daxil edin"  : lang === "ru" ? "Введите телефон" : "Enter phone number",
  };

  const waText = courseTitle
    ? (lang === "az" ? `Salam! "${courseTitle}" kursu barədə məlumat almaq istəyirəm.`
       : lang === "ru" ? `Здравствуйте! Хочу узнать о курсе «${courseTitle}».`
       : `Hello! I want to get information about the "${courseTitle}" course.`)
    : (lang === "az" ? "Salam! CodeKloud Academy kursları haqqında məlumat almaq istəyirəm."
       : lang === "ru" ? "Здравствуйте! Хочу узнать о курсах CodeKloud Academy."
       : "Hello! I want to get information about CodeKloud Academy courses.");

  const waUrl = `https://wa.me/994702607637?text=${encodeURIComponent(waText)}`;

  const handleSubmit = async () => {
    if (!form.name.trim()) { setError(L.nameErr); return; }
    if (!form.phone.trim()) { setError(L.phoneErr); return; }
    setError("");
    setSending(true);
    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE,
        process.env.REACT_APP_EMAILJS_TEMPLATE,
        {
          from_name: form.name,
          phone:     form.phone,
          email:     form.email,
          course:    courseTitle || "—",
          message:   form.message || "—",
        },
        process.env.REACT_APP_EMAILJS_KEY
      );
      setSent(true);
    } catch (err) {
      setError(lang === "az" ? "Xəta baş verdi. WhatsApp-dan yazın."
             : lang === "ru" ? "Ошибка. Напишите через WhatsApp."
             : "Error. Please contact via WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  const inputStyle = {
    width: "100%", background: "#f8fafc", border: "1px solid #cbd5e1",
    borderRadius: 10, padding: "12px 14px", color: "#1e293b", fontSize: 14,
    outline: "none", boxSizing: "border-box",
  };
  const labelStyle = { color: "#475569", fontSize: 13, marginBottom: 4, display: "block" };

  if (!show) return null;

  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 3000, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onClick={(e) => { if (e.target === e.currentTarget) { onClose(); setSent(false); setForm({ name:"", phone:"", email:"", message:"" }); } }}
    >
      <div style={{ backgroundImage: "url('/images/vizitkart3.jpg')", backgroundSize: "cover", backgroundPosition: "center", borderRadius: 20, width: "100%", maxWidth: 520, boxShadow: "0 30px 80px rgba(0,0,0,0.5)", overflow: "hidden", animation: "modalSlideUp 0.25s cubic-bezier(0.22,1,0.36,1)", maxHeight: "95vh", overflowY: "auto", position: "relative" }}>
        <div style={{ position: "absolute", inset: 0, background: "rgba(255,255,255,0.88)", borderRadius: 20 }} />
        <div style={{ position: "relative", zIndex: 1 }}>

        {/* Header */}
        <div style={{ background: accentColor || "#1e3a8a", padding: "18px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ color: "#fff", fontWeight: 900, fontSize: 16 }}>
            {lang === "az" ? "Əlaqə saxla" : lang === "ru" ? "Связаться" : "Contact Us"}
          </span>
          <button onClick={() => { onClose(); setSent(false); setForm({ name:"", phone:"", email:"", message:"" }); }}
            style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "50%", width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <X className="w-4 h-4" style={{ color: "#fff" }} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "24px 24px 20px" }}>
          {sent ? (
            <div style={{ textAlign: "center", padding: "32px 0" }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
              <p style={{ color: "#1e3a8a", fontWeight: 700, fontSize: 16, marginBottom: 8 }}>{L.success}</p>
            </div>
          ) : (
            <>
              <h2 style={{ color: "#1e3a8a", fontWeight: 900, fontSize: 22, marginBottom: 6 }}>{L.heading}</h2>
              <p style={{ color: "#64748b", fontSize: 13, marginBottom: 20 }}>{L.sub}</p>

              {/* Form */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={labelStyle}>{L.name}</label>
                  <input style={inputStyle} placeholder={L.name} value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                </div>
                <div>
                  <label style={labelStyle}>{L.phone}</label>
                  <input style={inputStyle} placeholder="+994 50 000 00 00" value={form.phone}
                    onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
                </div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>{L.email}</label>
                <input style={inputStyle} placeholder="example@gmail.com" value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>{L.message}</label>
                <textarea style={{ ...inputStyle, minHeight: 90, resize: "vertical" }} placeholder={L.message} value={form.message}
                  onChange={e => setForm(f => ({ ...f, message: e.target.value }))} />
              </div>

              {error && <p style={{ color: "#f87171", fontSize: 13, marginBottom: 10 }}>{error}</p>}

              {/* Submit */}
              <button onClick={handleSubmit} disabled={sending}
                style={{ width: "100%", background: "#1e3a8a", color: "#fff", border: "none", borderRadius: 12, padding: "13px 0", fontWeight: 800, fontSize: 15, cursor: "pointer", marginBottom: 12, opacity: sending ? 0.7 : 1 }}>
                {sending ? L.sending : L.submit}
              </button>

              {/* Divider */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <div style={{ flex: 1, height: 1, background: "#e2e8f0" }} />
                <span style={{ color: "#94a3b8", fontSize: 12 }}>
                  {lang === "az" ? "və ya" : lang === "ru" ? "или" : "or"}
                </span>
                <div style={{ flex: 1, height: 1, background: "#e2e8f0" }} />
              </div>

              {/* WhatsApp */}
              <a href={waUrl} target="_blank" rel="noopener noreferrer" onClick={onClose}
                style={{ width: "100%", background: "#25D366", color: "#fff", border: "none", borderRadius: 12, padding: "13px 0", fontWeight: 800, fontSize: 15, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, textDecoration: "none" }}>
                <MessageCircle style={{ width: 20, height: 20 }} />
                {L.waBtn}
              </a>
            </>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}

export { ApplyModal };
